import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  ShoppingCart, Plus, Trash2, Edit, Users, DollarSign, 
  CheckCircle2, Circle, Loader2, Package, Type, FileText, Hash
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export function ShoppingLists({ tripId, members, isOrganizer, hideHeader = false, onOpenDialog }) {
  const { api } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_for_everyone: true,
    items: []
  });
  const [newItemName, setNewItemName] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemUnit, setNewItemUnit] = useState('');
  const [newItemCost, setNewItemCost] = useState('');

  useEffect(() => {
    fetchLists();
  }, [tripId]);

  // Expor função para abrir dialog (para uso externo via callback)
  useEffect(() => {
    if (onOpenDialog) {
      onOpenDialog(() => setDialogOpen(true));
    }
  }, [onOpenDialog]);

  const fetchLists = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/shopping-lists`);
      setLists(response.data);
    } catch (error) {
      toast.error('Erro ao carregar listas de compras');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!newItemName.trim()) return;
    
    const newItem = {
      id: Date.now().toString(),
      name: newItemName,
      quantity: newItemQty || 1,
      unit: newItemUnit || null,
      estimated_cost: newItemCost ? parseFloat(newItemCost) : null,
      purchased: false
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    setNewItemName('');
    setNewItemQty(1);
    setNewItemUnit('');
    setNewItemCost('');
  };

  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Adicione um título para a lista');
      return;
    }
    
    try {
      if (editingList) {
        await api.put(`/trips/${tripId}/shopping-lists/${editingList.id}`, formData);
        toast.success('Lista atualizada!');
      } else {
        await api.post(`/trips/${tripId}/shopping-lists`, {
          ...formData,
          trip_id: tripId
        });
        toast.success('Lista criada!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchLists();
    } catch (error) {
      toast.error('Erro ao salvar lista');
    }
  };

  const handleToggleItem = async (listId, itemId) => {
    try {
      await api.post(`/trips/${tripId}/shopping-lists/${listId}/items/${itemId}/toggle`);
      fetchLists();
    } catch (error) {
      toast.error('Erro ao atualizar item');
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await api.delete(`/trips/${tripId}/shopping-lists/${listId}`);
      toast.success('Lista excluída');
      fetchLists();
    } catch (error) {
      toast.error('Erro ao excluir lista');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      is_for_everyone: true,
      items: []
    });
    setEditingList(null);
  };

  const openEditDialog = (list) => {
    setEditingList(list);
    setFormData({
      title: list.title,
      description: list.description || '',
      is_for_everyone: list.is_for_everyone,
      items: list.items || []
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dialog movido para fora do header */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingList ? 'Editar Lista' : 'Nova Lista de Compras'}
            </DialogTitle>
          </DialogHeader>
            
            <div className="space-y-2 py-4">
              <div className="py-2">
                <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Título da Lista *</label>
                <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                  <Type className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Compras para o churrasco"
                    className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                    data-testid="shopping-list-title"
                  />
                </div>
              </div>
              
              <div className="py-2">
                <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Descrição (opcional)</label>
                <div className="flex items-start gap-3 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                  <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-3" />
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Notas sobre a lista..."
                    rows={2}
                    className="w-full bg-transparent border-0 focus:ring-0 px-0 py-3 text-base placeholder:text-slate-400 text-slate-900 dark:text-white resize-none outline-none"
                  />
                </div>
              </div>
              
              {/* Add Items Section */}
              <div className="space-y-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <label className="text-xs text-slate-500 dark:text-slate-400 block">Adicionar Item</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                    <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input
                      placeholder="Nome do item"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-sm placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                      data-testid="shopping-item-name"
                    />
                  </div>
                  <div className="w-14 flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                    <Hash className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <input
                      type="number"
                      placeholder="Qtd"
                      value={newItemQty}
                      onChange={(e) => setNewItemQty(parseInt(e.target.value) || 1)}
                      className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-sm placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                  <div className="w-14 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                    <input
                      placeholder="Un"
                      value={newItemUnit}
                      onChange={(e) => setNewItemUnit(e.target.value)}
                      className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-sm placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                  <div className="w-16 flex items-center gap-1 border-b border-slate-200 dark:border-slate-700 focus-within:border-orange-500">
                    <DollarSign className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <input
                      type="number"
                      placeholder="R$"
                      value={newItemCost}
                      onChange={(e) => setNewItemCost(e.target.value)}
                      className="w-full bg-transparent border-0 focus:ring-0 px-0 py-2 text-sm placeholder:text-slate-400 text-slate-900 dark:text-white outline-none"
                    />
                  </div>
                  <Button type="button" onClick={handleAddItem} size="icon" className="rounded-full bg-orange-500 hover:bg-orange-600 h-8 w-8">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Items List */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.items.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <span className="text-sm">
                        {item.quantity} {item.unit && `${item.unit} de`} {item.name}
                        {item.estimated_cost && (
                          <span className="text-slate-500 ml-2">
                            (R$ {item.estimated_cost})
                          </span>
                        )}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItem(item.id)}
                        className="h-8 w-8 text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
              <Button onClick={handleSubmit} className="w-full bg-orange-500 hover:bg-orange-600 rounded-2xl h-14 text-base font-semibold">
                {editingList ? 'Salvar' : 'Criar Lista'}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="w-full rounded-2xl h-14 text-base font-semibold">
                Cancelar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Header condicional - só mostra se não receber hideHeader */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Listas de Compras
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Organize as compras do grupo e divida os custos
            </p>
          </div>
          <Button 
            className="bg-orange-500 hover:bg-orange-600" 
            data-testid="create-shopping-list-btn"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Lista
          </Button>
        </div>
      )}

      {/* Lists Grid */}
      {lists.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShoppingCart className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-slate-500 dark:text-slate-400 text-center">
              Nenhuma lista de compras criada ainda
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center mt-1">
              Crie uma lista para organizar as compras do grupo
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <AnimatePresence>
            {lists.map((list, index) => {
              const progress = list.items_total > 0 
                ? (list.items_purchased / list.items_total) * 100 
                : 0;
              
              return (
                <motion.div
                  key={list.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-md transition-shadow dark:bg-slate-800/50">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Package className="w-5 h-5 text-orange-500" />
                            {list.title}
                          </CardTitle>
                          {list.description && (
                            <CardDescription className="mt-1">
                              {list.description}
                            </CardDescription>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(list)}
                            className="h-8 w-8"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir lista?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteList(list.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {/* Progress */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500 dark:text-slate-400">
                            {list.items_purchased} de {list.items_total} itens
                          </span>
                          <span className="font-medium text-orange-600">
                            {Math.round(progress)}%
                          </span>
                        </div>
                        <Progress value={progress} className="h-2" />
                      </div>
                      
                      {/* Cost Summary */}
                      <div className="flex items-center justify-between text-sm p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <span className="text-slate-600 dark:text-slate-400">
                          <DollarSign className="w-4 h-4 inline mr-1" />
                          Estimado: R$ {list.total_estimated?.toFixed(2) || '0.00'}
                        </span>
                        <span className="text-green-600 dark:text-green-400">
                          Gasto: R$ {list.total_actual?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                      
                      {/* Items Checklist */}
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {list.items?.map((item) => (
                          <div
                            key={item.id}
                            className={cn(
                              "flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer",
                              item.purchased 
                                ? "bg-green-50 dark:bg-green-900/20" 
                                : "hover:bg-slate-50 dark:hover:bg-slate-800"
                            )}
                            onClick={() => handleToggleItem(list.id, item.id)}
                          >
                            <Checkbox 
                              checked={item.purchased}
                              className="data-[state=checked]:bg-green-500"
                            />
                            <span className={cn(
                              "flex-1 text-sm",
                              item.purchased && "line-through text-slate-400"
                            )}>
                              {item.quantity} {item.unit && `${item.unit} `}{item.name}
                            </span>
                            {item.estimated_cost && (
                              <span className="text-xs text-slate-500 dark:text-slate-400">
                                R$ {item.estimated_cost}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Members Badge */}
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {list.is_for_everyone 
                            ? 'Para todos os participantes' 
                            : `${list.assigned_members?.length || 0} membros`
                          }
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
